import { create } from 'zustand';

/**
 * Drives the app-wide action-verification popup (`KycActionGate`). Unlike the
 * identity gate, an action never blocks or replays a request — it's an extra
 * check — so there are no awaited resolvers; open/close is fire-and-forget.
 *
 * Both entry points (the Settings list and the proactive auto-gate) open the
 * gate through this single store, so exactly one Sumsub WebSDK instance is ever
 * mounted regardless of where the open came from.
 */
interface KycActionGateState {
  /** externalActionId the gate should run, or null when closed. */
  activeActionId: string | null;
  /** Bumps on each open so the runner remounts with clean token/error state,
   *  even if the same action is re-opened. */
  openToken: number;
  open: (externalActionId: string) => void;
  close: () => void;
}

const useKycActionGateStore = create<KycActionGateState>((set) => ({
  activeActionId: null,
  openToken: 0,
  open: (externalActionId) => set((s) => ({ activeActionId: externalActionId, openToken: s.openToken + 1 })),
  close: () => set({ activeActionId: null }),
}));

/** The action the gate is currently running, or null. */
export const useKycActiveActionId = () => useKycActionGateStore((s) => s.activeActionId);

/** Increments whenever the gate is (re)opened — used as a remount key. */
export const useKycActionOpenToken = () => useKycActionGateStore((s) => s.openToken);

/** Open the gate for an action (hook form). */
export const useOpenKycActionGate = () => useKycActionGateStore((s) => s.open);

/** Close the gate (hook form). */
export const useCloseKycActionGate = () => useKycActionGateStore((s) => s.close);

/** Non-hook accessor for the proactive auto-gate (outside React). */
export const openKycActionGateImperative = (externalActionId: string) =>
  useKycActionGateStore.getState().open(externalActionId);

/** Non-hook close (tests, imperative teardown). */
export const closeKycActionGateImperative = () => useKycActionGateStore.getState().close();
